import React, { useRef } from 'react';
import { shortcutLabelString } from '../../config/shortcuts';
import { Undo2, Redo2, Lock, Settings } from 'lucide-react';
import AvatarStack from '../ui/AvatarStack';
import Tooltip from '../ui/Tooltip';
import BoardSummaryPill from './BoardSummaryPill';
import BoardViewToggle from './BoardViewToggle';
import useToolbarCompact from './useToolbarCompact';
import './BoardToolbar.css';

/** The open board's ONE toolbar row (owner, 13.10.2026: no title row — the
 *  tab already names the board): undo / redo, then `children` (the search,
 *  filter chips and "N of M shown"), then on the right the view-only badge,
 *  who else is viewing, the progress pill, the Kanban / List switch and the
 *  board settings gear. `progress` = boardProgress(board). */
export default function BoardToolbar({ board, boardReadOnly, readOnlyReason = null, boardViewers, canRedo, canUndo, openEditBoardModal, progress, redo, undo, updateBoard, viewMode, children }) {
  // Never a second row (owner, 13.10.2026): too narrow → icons only.
  const ref = useRef(null);
  const compact = useToolbarCompact(ref);
  return (
    <div className={`task-board-toolbar${compact ? ' task-board-toolbar--compact' : ''}`} ref={ref}>
      <Tooltip content={`Undo (${shortcutLabelString({ mod: true, key: 'z' })})`} side="bottom">
        <button type="button" className="task-board-toolbar-btn task-board-toolbar-btn--ghost" aria-label="Undo" disabled={!canUndo} onClick={undo}>
          <Undo2 size={15} />
        </button>
      </Tooltip>
      <Tooltip content={`Redo (${shortcutLabelString({ mod: true, shift: true, key: 'z' })})`} side="bottom">
        <button type="button" className="task-board-toolbar-btn task-board-toolbar-btn--ghost" aria-label="Redo" disabled={!canRedo} onClick={redo}>
          <Redo2 size={15} />
        </button>
      </Tooltip>
      <span className="task-board-toolbar-divider" aria-hidden="true" />
      {/* The filters take the free width, so everything after them sits right. */}
      <div className="task-board-toolbar-filters">{children}</div>
      {boardReadOnly && (
        <Tooltip content={readOnlyReason || 'You have viewer access on this team board'} side="bottom">
          <span className="task-board-view-only">
            <Lock size={11} /> View only
          </span>
        </Tooltip>
      )}
      <AvatarStack members={boardViewers} />
      <BoardSummaryPill progress={progress} />
      <BoardViewToggle
        viewMode={viewMode}
        onChange={(mode) => updateBoard((prev) => ({ ...prev, viewMode: mode }), { history: false })}
      />
      {!boardReadOnly && (
        <Tooltip content="Board settings" side="bottom">
          <button type="button" className="task-board-toolbar-btn task-board-toolbar-btn--square" aria-label="Board settings" onClick={() => openEditBoardModal(board)}>
            <Settings size={15} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
